import React, { useEffect, useState, useCallback } from "react";
import styles from "./Punches.module.css";
import { Helmet } from "react-helmet";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRefresh,
  faCircleChevronLeft,
  faCircleChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { getPunches } from "../../api/api";
import classNames from "classnames";
import { Tooltip } from "react-tooltip";

export default function Punches({ schedule, businessId }) {
  const [loading, setLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const [punches, setPunches] = useState([]);
  const initialStartDate = new Date();
  initialStartDate.setDate(initialStartDate.getDate() - 7);
  const [startTime, setStartTime] = useState(initialStartDate);
  const [endTime, setEndTime] = useState(new Date());
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchData = async () => {
    try {
      setLoading(true);
      setIsError(false)
      if (schedule) {
        const startTimeISO = startTime.toISOString();
        const endTimeISO = endTime.toISOString();
        const data = await getPunches(
          businessId,
          schedule.id,
          startTimeISO,
          endTimeISO,
          currentPage
        );
        setPunches(data.punches);
        const newTotalPages = Math.max(1, Math.ceil(data.total_count / data.page_limit));
        setTotalPages(newTotalPages);
        if (newTotalPages < currentPage) {
          setCurrentPage(newTotalPages);
        }
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching punches list:", error);
      setPunches([]);
      setLoading(false);
      setIsError(true)
    }
  };

  useEffect(() => {
    fetchData();
  }, [businessId, schedule?.id, startTime, endTime, currentPage]);

  const handleFetchClick = async () => {
    try {
      fetchData();
    } catch (error) {
      console.error("Error fetching punches:", error);
    }
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  function PunchesContent( {loading, schedule, isError, punches}) {
    if (loading) {
      return (
        <tr>
          <td colSpan="8" className={styles.spinner}>
            <div className={styles.loader}></div>
          </td>
        </tr>
      );
    } else if (!schedule) {
      return (
        <tr className={styles.noData}>
          <td colSpan="8">No schedule selected</td>
        </tr>
      );
    } else if (isError) {
      return (
        <tr className={styles.noData}>
          <td colSpan="8">Error retrieving punches</td>
        </tr>
      );
    } else if (!Array.isArray(punches) || punches?.length === 0) {
      return (
        <tr className={styles.noData}>
          <td colSpan="8">No punches found</td>
        </tr>
      );
    } else {
      return punches?.map((punch, index) => (
        <tr className={styles.bodyTr} key={punch.id}>
          <td>{punch.badge_id}</td>
          <td>{punch.action_type}</td>
          <td>{new Date(punch.punch_timestamp).toLocaleString(undefined, {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
          })}</td>
          <td>{punch.status}</td>
          <td>{punch.reason}</td>
          <td>{punch.mode}</td>
          <td>{new Date(punch.created_at).toLocaleString()}</td>
        </tr>
      ));
    }
  }

  const prevButtonClasses = classNames(styles.prevButton, {
    [styles.disabled]: currentPage === 1,
  });

  const nextButtonClasses = classNames(styles.nextButton, {
    [styles.disabled]: currentPage === totalPages || punches?.length === 0,
  });

  return (
    <div className={styles.container}>
      <Helmet>
        <title>Punches | LIFELENZ</title>
      </Helmet>
      <section className={styles.tableHeader}>
        <div className={styles.headerContent}>
          <span className={styles.headerSpan}>
            Store | Punches
          </span>
          <span className={styles.headerDisclaimer}>Note: These are raw punches from the clocks, they may not reflect the final state of shifts in LIFELENZ.</span>
          <div
            className={styles.datePickers}
            data-tooltip-id="my-tooltip"
            data-tooltip-content="Select the start date and end date"
            data-tooltip-place="left"
          >
            <div>
              <DatePicker
                className={styles.datePicker}
                selected={startTime}
                onChange={(date) => setStartTime(date)}
                showTimeSelect
                dateFormat="PPp"
                placeholderText="Start Date"
              />
            </div>

            <p className={styles.p}>- </p>
            <div>
              <DatePicker
                className={styles.datePicker}
                selected={endTime}
                onChange={(date) => setEndTime(date)}
                showTimeSelect
                dateFormat="PPp"
                minDate={startTime}
                placeholderText="End Date"
              />
            </div>

            <FontAwesomeIcon
              icon={faRefresh}
              alt="refresh"
              className={styles.fetchButton}
              onClick={handleFetchClick}
            />
          </div>
        </div>
      </section>
      <main className={styles.mainTable}>
        <section className={styles.tableBody}>
          <table className={styles.punchTable}>
            <thead>
              <tr>
                <th>Badge Id</th>
                <th>Punch Type</th>
                <th>Punch Time</th>
                <th>Status</th>
                <th>Warning message</th>
                <th>Processing Mode</th>
                <th>Received Time</th>
              </tr>
            </thead>
            <tbody>
              <PunchesContent loading={loading} schedule={schedule} isError={isError} punches={punches} />
            </tbody>
          </table>
        </section>
        <section className={styles.tableFooter}>
          <div className={styles.pagination}>
            <FontAwesomeIcon
              icon={faCircleChevronLeft}
              alt="previous"
              title="Previous"
              className={prevButtonClasses}
              onClick={() => {
                if (!(currentPage === 1)) {
                  handlePageChange(currentPage - 1);
                }
              }}
            />
            <span className={styles.pageSpan}>{currentPage} of {totalPages}</span>
            <FontAwesomeIcon
              icon={faCircleChevronRight}
              alt="next"
              title="Next"
              className={nextButtonClasses}
              onClick={() => {
                if (!(currentPage === totalPages || punches?.length === 0)) {
                  handlePageChange(currentPage + 1);
                }
              }}
            />
          </div>
        </section>
      </main>
      <Tooltip />
    </div>
  );
}
